// Texto 25, etapa 2: "Recebimentos" dentro de cada cliente (Meus clientes).
import { useState } from 'react';
import { Alert, Modal, Pressable, Text, TextInput, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, TopoProps } from './tipos';
import { dataBr, din, junta, num } from './util';
import { N_, t } from '../lib/i18n';
import { C } from '../components/theme';
import { today } from '../lib/date';
import { lerValor, dinheiro } from '../lib/moeda';
import { whatsapp } from '../lib/mapa';
import { mudarCampos } from '../store/modulos';
import { gerarECompartilharPdf } from '../lib/pdf';
import { marcarAbaAtual } from '../lib/relatorios';
import { semBloqueio } from '../auth/AuthContext';
import { Chip } from '../components/ui';
import { FORMAS, TAXAS_EDITAVEIS, atrasada, confRec, contarCobranca, falta, gerarParcelas, liquido, mudarConfRec, msgCobranca, parcelasDe, proxVenc,
  quitada, receberParcela, MODOS, type Parcela } from '../lib/recebimentos';

const ESTRUTURA = ['como', 'valor', 'n', 'primeiroVenc', 'sinal', 'sinalPct', 'sinalData', 'restanteData', 'naEntrega', 'data'];
const estrutura = (c: Record<string, any>) => JSON.stringify(ESTRUTURA.map(k => c[k] ?? ''));

/** Quanto recebeu (parcial ou tudo). */
function QuantoRecebeu({ p, moeda, cor, onOk, onFechar }: { p: Parcela; moeda: string; cor: string; onOk: (v: number) => void; onFechar: () => void }) {
  const [txt, setTxt] = useState(String(falta(p)).replace('.', ','));
  const v = lerValor(txt), ok = isFinite(v) && v > 0;
  return (
    <Modal transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 12, alignSelf: 'center', width: '100%', maxWidth: 380 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{t('Quanto recebeu?')} · {p.rotulo}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Falta {v}. Pode ser só uma parte.', { v: dinheiro(falta(p), moeda) })}</Text>
          <TextInput value={txt} onChangeText={setTxt} keyboardType="decimal-pad" autoFocus selectTextOnFocus inputAccessoryViewButtonLabel={t('Concluído')} accessibilityLabel={t('Quanto recebeu?')}
            style={{ backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, color: C.text, fontSize: 18, fontWeight: '700' }} />
          <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
            <Pressable onPress={onFechar} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Cancelar')}</Text></Pressable>
            <Pressable disabled={!ok} onPress={() => { onOk(Math.round(v * 100) / 100); onFechar(); }} hitSlop={8} style={{ paddingHorizontal: 18, paddingVertical: 8, borderRadius: 10, backgroundColor: ok ? cor : C.card2 }}>
              <Text style={{ color: ok ? '#fff' : C.mute, fontWeight: '800' }}>{t('Marcar como pago')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** D8: recibo em PDF (de uma parcela recebida, inclusive o sinal). */
async function recibo(cliente: Reg | null, r: Reg, p: Parcela, moeda: string) {
  const conf = confRec();
  const quem = conf.nomeRecibo || t('(seu nome ou sua marca, em Como você recebe?)');
  marcarAbaAtual('clientes');
  await gerarECompartilharPdf(t('Recibo {c}', { c: String(cliente?.c.nome ?? '') }).trim(), [
    { t: 'titulo', texto: t('Recibo') },
    { t: 'par', texto: t('Recebi de {c} a quantia de {v}, referente a {o}.', { c: String(cliente?.c.nome ?? ''), v: dinheiro(Number(p.recebido) || 0, moeda), o: junta(String(r.c.desc ?? ''), p.rotulo) }) },
    { t: 'par', texto: junta(t(String(r.c.forma ?? '')), p.pagoEm && dataBr(p.pagoEm)) },
    { t: 'espaco', h: 40 },
    { t: 'assinatura', linhas: [quem] },
  ], quem);
}

/** D11: as parcelas (ou o sinal) com Cobrar pelo WhatsApp · Marcar como pago · Gerar recibo. */
function PainelReceb({ r, ctx, pode }: TopoProps) {
  const [receber, setReceber] = useState<Parcela | null>(null);
  const cliente = ctx.porId(r.pai);
  const ps = parcelasDe(r);
  const moeda = ctx.moeda;
  const liq = liquido(r.c);
  const autoriza = !!cliente?.c.autoriza;
  return (
    <View style={{ gap: 8 }}>
      {liq.taxa > 0 && <Text style={{ color: C.sub, fontSize: 12 }}>💳 {t('{v} no {f} → recebe {l}', { v: din(r.c.valor, moeda), f: t(liq.chave), l: din(liq.valor, moeda) })}</Text>}
      {ps.map(p => {
        const at = atrasada(p), ok = quitada(p);
        return (
          <View key={p.id} style={{ backgroundColor: C.card, borderRadius: 12, padding: 10, gap: 6, borderWidth: 1, borderColor: at ? C.danger : C.line }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 16 }}>{ok ? '✅' : at ? '🔴' : '🕒'}</Text>
              <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>{p.rotulo} · {din(p.valor, moeda)}</Text>
              <Text style={{ color: at ? C.danger : C.sub, fontSize: 12 }}>{p.venc ? dataBr(p.venc) : t('na entrega')}</Text>
            </View>
            {!ok && Number(p.recebido) > 0 && <Text style={{ color: C.warn, fontSize: 12 }}>{t('Pagou {a} de {b}, faltam {c}', { a: din(p.recebido, moeda), b: din(p.valor, moeda), c: din(falta(p), moeda) })}</Text>}
            {ok && <Text style={{ color: C.ok, fontSize: 12 }}>{t('Recebido em {d}', { d: dataBr(p.pagoEm) })}</Text>}
            {pode && (
              <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                {!ok && <Chip small cor={ctx.cor} on={false} label={'💬 ' + t('Cobrar pelo WhatsApp')} onPress={() => {
                  if (!autoriza) return Alert.alert(t('Sem autorização'), t('Este cliente não autorizou receber mensagens (ficha do cliente › Relacionamento).'));
                  if (!cliente?.c.telefone) return Alert.alert(t('Sem telefone'), t('Coloque o telefone na ficha do cliente.'));
                  contarCobranca(r.id, p.id); whatsapp(String(cliente.c.telefone), msgCobranca(cliente, p, moeda));
                }} />}
                {!ok && <Chip small cor={ctx.cor} on label={'✓ ' + t('Marcar como pago')} onPress={() => setReceber(p)} />}
                {Number(p.recebido) > 0 && <Chip small cor={ctx.cor} on={false} label={'🧾 ' + t('Gerar recibo')} onPress={() => { semBloqueio(() => recibo(cliente, r, p, moeda)).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); }} />}
                {Number(p.recebido) > 0 && r.c.como !== 'À vista' && <Chip small cor={ctx.cor} on={false} label={'↺ ' + t('Desfazer')} onPress={() => receberParcela('clientes', r.id, p.id, 0)} />}
              </View>
            )}
          </View>
        );
      })}
      {!!receber && <QuantoRecebeu p={receber} moeda={moeda} cor={ctx.cor} onFechar={() => setReceber(null)} onOk={v => receberParcela('clientes', r.id, receber.id, v)} />}
    </View>
  );
}

/** D1/D3/C8/D7/D8: "Como você recebe?" (na lista de Meus clientes, recolhido). */
export function AjustesRecebimento({ cor }: { cor: string }) {
  const [aberto, setAberto] = useState(false);
  const conf = confRec();
  const [taxa, setTaxa] = useState<Record<string, string>>({});
  if (!aberto) return (
    <Pressable onPress={() => setAberto(true)} hitSlop={6}><Text style={{ color: C.sub, fontSize: 12 }}>⚙️ {t('Como você recebe?')} · {conf.modos.map(m => t(m)).join(', ')} ▸</Text></Pressable>);
  const alternar = (m: string) => { const l = conf.modos.includes(m) ? conf.modos.filter(x => x !== m) : [...conf.modos, m]; if (l.length) mudarConfRec({ modos: MODOS.filter(x => l.includes(x)) }); };
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 10 }}>
      <Pressable onPress={() => setAberto(false)}><Text style={{ color: C.text, fontWeight: '800' }}>▾ ⚙️ {t('Como você recebe?')}</Text></Pressable>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>{MODOS.map(m => <Chip key={m} small cor={cor} on={conf.modos.includes(m)} label={t(m)} onPress={() => alternar(m)} />)}</View>
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Só "À vista": a tela fica limpa, sem a prazo, parcelas nem vermelho.')}</Text>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>{t('Taxa da maquininha (%)')}</Text>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {TAXAS_EDITAVEIS.map(k => (
          <View key={k} style={{ alignItems: 'center', gap: 2 }}>
            <Text style={{ color: C.sub, fontSize: 11 }}>{t(k)}</Text>
            <TextInput value={taxa[k] ?? (conf.taxas[k] != null ? String(conf.taxas[k]).replace('.', ',') : '')} onChangeText={x => setTaxa(s => ({ ...s, [k]: x }))}
              onBlur={() => { const v = lerValor(taxa[k] ?? ''); const n = { ...conf.taxas }; if (isFinite(v) && v > 0) n[k] = Math.round(v * 100) / 100; else delete n[k]; mudarConfRec({ taxas: n }); }}
              keyboardType="decimal-pad" placeholder="0" placeholderTextColor={C.mute} accessibilityLabel={t('Taxa {k}', { k: t(k) })}
              style={{ width: 60, backgroundColor: C.card2, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 6, color: C.text, textAlign: 'center' }} />
          </View>))}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.text, flex: 1 }}>{t('Cliente sumido depois de (dias sem comprar)')}</Text>
        <TextInput defaultValue={String(conf.sumidoDias)} onEndEditing={e => mudarConfRec({ sumidoDias: Math.max(7, Math.round(num(e.nativeEvent.text)) || 60) })} keyboardType="number-pad"
          style={{ width: 60, backgroundColor: C.card2, borderRadius: 8, padding: 6, color: C.text, textAlign: 'center' }} accessibilityLabel={t('Dias para considerar sumido')} />
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.text, flex: 1 }}>{t('Lembretes de cobrança por parcela atrasada')}</Text>
        {[0, 1, 2, 3].map(n => <Chip key={n} small cor={cor} on={conf.lembretes === n} label={n ? String(n) : t('Nenhum')} onPress={() => mudarConfRec({ lembretes: n })} />)}
      </View>
      <View style={{ gap: 4 }}>
        <Text style={{ color: C.text }}>{t('Nome ou marca no recibo')}</Text>
        <TextInput defaultValue={conf.nomeRecibo} onEndEditing={e => mudarConfRec({ nomeRecibo: e.nativeEvent.text.trim().slice(0, 80) })} placeholder={t('Ex.: Ana Beatriz Doces')} placeholderTextColor={C.mute}
          style={{ backgroundColor: C.card2, borderRadius: 8, padding: 8, color: C.text }} />
      </View>
    </View>
  );
}

export const receb: Colecao = {
  id: 'receb', pai: 'cliente', nome: N_('Recebimentos'), um: N_('Venda ou serviço'),
  semDuplicar: r => parcelasDe(r).some(p => Number(p.recebido) > 0),   // texto 26 (B7): pagamento já feito
  icone: r => (parcelasDe(r).every(quitada) ? '✅' : parcelasDe(r).some(p => atrasada(p)) ? '🔴' : '🕒'),
  campos: [
    { k: 'desc', r: N_('O que foi vendido ou feito'), t: 'texto', ph: N_('Ex.: Bolo de aniversário') },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'como', r: N_('Como paga'), t: 'opcao', traduzir: true, opDe: () => confRec().modos },
    { k: 'forma', r: N_('Forma de pagamento'), t: 'opcao', op: FORMAS },
    { k: 'n', r: N_('Parcelas'), t: 'numero', mostrar: c => c.como === 'A prazo' || c.forma === 'Cartão de crédito parcelado' },
    { k: 'primeiroVenc', r: N_('Vencimento da 1ª parcela'), t: 'data', semHoje: true, mostrar: c => c.como === 'A prazo' },
    { k: 'sinalPct', r: N_('Sinal (%)'), t: 'numero', mostrar: c => c.como === 'Sinal + restante', dica: N_('Ou escreva o valor do sinal logo abaixo.') },
    { k: 'sinal', r: N_('Sinal (valor)'), t: 'dinheiro', mostrar: c => c.como === 'Sinal + restante' },
    { k: 'sinalData', r: N_('Data do sinal'), t: 'data', mostrar: c => c.como === 'Sinal + restante' },
    { k: 'naEntrega', r: N_('Restante na entrega'), t: 'sim', interruptor: true, mostrar: c => c.como === 'Sinal + restante' },
    { k: 'restanteData', r: N_('Data do restante'), t: 'data', semHoje: true, mostrar: c => c.como === 'Sinal + restante' && !c.naEntrega },
    { k: 'proxVenc', r: N_('Próximo vencimento'), t: 'data', aviso: [1], mostrar: () => false },
  ],
  titulo: r => String(r.c.desc || t('Venda')),
  sub: (r, ctx) => {
    const ps = parcelasDe(r), deve = ps.reduce((s, p) => s + falta(p), 0);
    const at = ps.some(p => atrasada(p));
    if (r.c.como === 'Sinal + restante') {
      const s = ps[0], rest = ps[1];
      return junta(din(r.c.valor, ctx.moeda), s && quitada(s) ? t('Sinal pago') : t('Sinal a receber'), deve > 0 && t('falta {v}{q}', { v: din(deve, ctx.moeda), q: rest && !rest.venc ? ' ' + t('na entrega') : '' }));
    }
    if (r.c.como === 'A prazo') return junta(din(r.c.valor, ctx.moeda), deve > 0 ? t('deve {v}', { v: din(deve, ctx.moeda) }) : t('quitado'), at && t('parcela atrasada'));
    const l = liquido(r.c);
    return junta(t('À vista'), din(r.c.valor, ctx.moeda), r.c.forma && t(r.c.forma), l.taxa > 0 && t('recebe {v}', { v: din(l.valor, ctx.moeda) }));
  },
  data: 'data', ordem: 'data-desc', pagina: true, painelTopo: PainelReceb, anexos: true, semEnviarLista: false,
  novo: () => ({ data: today(), como: confRec().modos[0], forma: 'Pix', n: 1, sinalPct: 50, naEntrega: true }),
  // as parcelas são (re)feitas quando muda o jeito de pagar, se ainda não houve nada recebido fora do à vista
  aoSalvar: (r, ctx) => {
    const ps = parcelasDe(r);
    const temPago = r.c.como !== 'À vista' && ps.some(p => Number(p.recebido) > 0);
    if (!ps.length || (estrutura(r.c) !== r.c.estrutura && !temPago)) {
      const novas = gerarParcelas(r.c);
      mudarCampos(ctx.mod, r.id, { parcelas: novas, estrutura: estrutura(r.c), proxVenc: proxVenc(novas) });
    } else if (estrutura(r.c) !== r.c.estrutura && temPago) {
      Alert.alert(t('Parcelas mantidas'), t('Já há pagamento registrado: as parcelas não foram refeitas. Para mudar, desfaça os pagamentos primeiro.'));
    }
  },
  vazio: N_('Registre o que o cliente comprou ou o serviço que você fez, e como ele paga.'),
};
